import { useMemo } from 'react';

import { faTowerCell } from '@fortawesome/pro-solid-svg-icons';
import {
  BarElement,
  CategoryScale,
  Chart,
  ChartData,
  ChartOptions,
  LinearScale,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';

// eslint-disable-next-line import/no-relative-packages
import { DeviceTypeTotals } from '../../../../universal-dashboard-api/src/Data-Processor/Entities/Interfaces/Summary';
import {
  createFirstLastAnnotations,
  getFontSizeForWindow,
} from '../../utils/annotations';
import { getVariable } from '../../utils/style';
import { Card } from './Card';
import { Header } from './Header';

Chart.register(BarElement, CategoryScale, LinearScale, annotationPlugin);

type AssetGrowthCardProps = {
  data?: DeviceTypeTotals;
  loading?: boolean;
};

const LABEL_BOTTOM_PADDING = 5;

export function AssetGrowthCard({ data, loading }: AssetGrowthCardProps) {
  const { assetGrowth = [] } = data ?? {};

  const chartData: ChartData<'bar'> = useMemo(() => {
    const labels = assetGrowth.map(({ month }) => month);

    return {
      labels,
      datasets: [
        {
          label: 'Dataset 1',
          data: assetGrowth.map(({ totalDevices }) => totalDevices),
          borderColor: getVariable('--green-30'),
          borderWidth: 2,
          backgroundColor: 'rgba(8, 189, 186, 0.4)',
          fill: true,
          pointBorderWidth: 0,
          pointRadius: 0,
        },
      ],
    };
  }, [assetGrowth]);

  const options: ChartOptions<'bar'> = useMemo(() => {
    const maxYValue = Math.max(
      ...assetGrowth.map(({ totalDevices }) => totalDevices),
    );
    const fontSize = getFontSizeForWindow();

    return {
      animation: false,
      maintainAspectRatio: false,
      plugins: {
        annotation: {
          annotations: createFirstLastAnnotations({
            arrayLength: assetGrowth.length,
            firstPosition: 'center',
            firstY: assetGrowth[0]?.totalDevices,
            fontSize,
            labelBottomPadding: LABEL_BOTTOM_PADDING,
            lastPosition: 'center',
            lastY: assetGrowth[assetGrowth.length - 1]?.totalDevices,
          }),
          clip: false,
        },
      },
      responsive: true,
      scales: {
        x: {
          display: false,
        },
        y: {
          display: false,
          max: maxYValue,
        },
      },
      layout: {
        padding: {
          top: fontSize + LABEL_BOTTOM_PADDING,
          right: 5,
          left: 5,
        },
      },
    };
  }, [assetGrowth]);

  return (
    <Card className="gap-2">
      <Header
        border
        icon={faTowerCell}
        loading={loading}
        title="Controllable asset growth (last 12 months)"
      />
      <div className="h-16">
        {!loading && <Bar data={chartData} options={options} />}
      </div>
    </Card>
  );
}
